import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { MatLegacyCheckboxChange as MatCheckboxChange } from '@angular/material/legacy-checkbox';
import { MatLegacyPaginator as MatPaginator } from '@angular/material/legacy-paginator';
import { MatSort } from '@angular/material/sort';
import { BehaviorSubject, Observable, of, throwError } from 'rxjs';
import { filter, map, switchMap, take, tap } from 'rxjs/operators';
import { ProjectSheet } from './sheets.types';

@Injectable({
  providedIn: 'root',
})
export class ProjectSheetsService {
  // Private
  private _projectSheets: BehaviorSubject<ProjectSheet[] | null>;
  private _projectSheet: BehaviorSubject<ProjectSheet | null>;
  // Observables that can be executable from outside
  selectedNoteChange: BehaviorSubject<any>;

  private _projectId: string;
  private _accountId: string;

  /**
   * Constructor
   *
   * @param {HttpClient} _httpClient
   */
  constructor(private _httpClient: HttpClient) {
    // Set the private defaults
    this._projectSheets = new BehaviorSubject(null);
    this._projectSheet = new BehaviorSubject(null);
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Accessors
  // -----------------------------------------------------------------------------------------------------

  /**
   * Getter for projectSheets
   */
  get projectSheets$(): Observable<ProjectSheet[]> {
    return this._projectSheets.asObservable();
  }

  /**
   * Getter for contact
   */
  get projectSheet$(): Observable<ProjectSheet> {
    return this._projectSheet.asObservable();
  }

  set accountId(val: string) {
    this._accountId = val;
  }

  set projectId(val: string) {
    this._projectId = val;
  }

  get projectId() {
    return this._projectId;
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Public methods
  // -----------------------------------------------------------------------------------------------------

  /**
   * Get contacts
   *
   * @param sortField
   * @param sortDirection
   */
  getProjectSheets(): Observable<ProjectSheet[]> {
    return this._httpClient.get<any>(`projects/${this.projectId}/project_sheets`).pipe(
      tap((response: any) => {
        this._projectSheets.next(response.project_sheets);
      }),
    );
  }

  /**
   * Get project by id
   */
  getProjectSheetById(id: string): Observable<ProjectSheet> {
    return this._httpClient.get<any>(`projects/${this.projectId}/project_sheets/${id}`).pipe(
      map((response) => {
        this._projectSheet.next(response.project_sheet);
        return response.project_sheet;
      }),
    );
  }

  createProjectSheet(projectSheet: ProjectSheet): Observable<ProjectSheet> {
    return this.projectSheets$.pipe(
      take(1),
      switchMap((projectSheets) =>
        this._httpClient
          .post<any>(`projects/${this.projectId}/project_sheets`, {
            project_sheet: projectSheet,
          })
          .pipe(
            map((reponse) => {
              // Update the contacts with the new contact
              this._projectSheets.next([reponse.project_sheet, ...projectSheets]);

              // Return the new contact
              return reponse.project_sheet;
            }),
          ),
      ),
    );
  }

  updateProjectSheet(projectSheet: ProjectSheet): Observable<ProjectSheet> {
    return this.projectSheets$.pipe(
      take(1),
      switchMap((projectSheets) =>
        this._httpClient
          .put<any>(`projects/${this.projectId}/project_sheets/${projectSheet.id}`, {
            project_sheet: projectSheet,
          })
          .pipe(
            map((response) => {
              let updatedProjectSheet = response.project_sheet;

              // Find the index of the updated task
              const index = projectSheets.findIndex((item) => item.id === projectSheet.id);

              // Update the contact
              projectSheets[index] = updatedProjectSheet;

              // Update the contacts
              this._projectSheets.next(projectSheets);

              // Return the updated contact
              return updatedProjectSheet;
            }),
            switchMap((updatedProjectSheet) =>
              this.projectSheet$.pipe(
                take(1),
                filter((item) => item && item.id === updatedProjectSheet.id),
                tap(() => {
                  // Update the task if it's selected
                  this._projectSheet.next(updatedProjectSheet);

                  // Return the updated task
                  return updatedProjectSheet;
                }),
              ),
            ),
          ),
      ),
    );
  }

  deleteProjectSheet(id: number): Observable<any> {
    return this.projectSheets$.pipe(
      take(1),
      switchMap((projectSheets) =>
        this._httpClient.delete<any>(`projects/${this.projectId}/project_sheets/${id}`).pipe(
          map((reponse) => {
            // Find the index of the deleted label within the labels
            const index = projectSheets.findIndex((item) => item.id === id);

            // Delete the label
            projectSheets.splice(index, 1);

            // Update the labels
            this._projectSheets.next(projectSheets);

            // Return the deleted status
            return reponse.project_sheet;
          }),
        ),
      ),
    );
  }
}
